import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import AuthContext from "../../hooks/authContext";
import { useNavigate } from "react-router";

const TourContext = createContext(undefined);

const tourData = {
  'getting-started': [
    {
      id: "help-navigation",
      title: "Step 1: Getting Started",
      description:
        "Learn how to use ProTeams by clicking help and following our tours menus, or by reviewing our knowledge base. ",
      target: '[data-tour="help-nav"]',
      page: "/dashboard",
    },
    {
      id: "tour-navigation",
      title: "Step 2: Guided Tours Available",
      description:
        "We’ve prepared tours for Dashboard, Appointments, Clients, Supplies, and more. You can start them anytime from the Tour section.",
      target: '[data-tour="tour-nav"]',
      page: "/dashboard",
    },
  ],
  dashboard: [
    {
      id: "overview-section",
      title: "Step 1: Overview",
      description:
        "The overview modules provide you with a snapshot of your team.",
      target: '[data-tour="overview-section"]',
      page: "/dashboard",
    },
    {
      id: "daily-summary",
      title: "Step 2: Daily Summary",
      description:
        "View scheduled work, employee on time status, update shifts, and view full details by client in expanded details.",
      target: '[data-tour="daily-summary"]',
      page: "/dashboard",
    },
    {
      id: "communication-messages",
      title: "Step 3: Communication Channel - Messages",
      description:
        "Quickly review team messages, client messages and system errors.",
      target: '[data-tour="communication-messages"]',
      page: "/dashboard",
    },
    {
      id: "date-fields",
      title: "Step 4: Date Fields",
      description:
        "The date field will default to the current day. To go forward or back in time simply click the arrow or select the date field to view more dates.",
      target: '[data-tour="date-navigation"]',
      page: "/dashboard",
    },
  ],
  appointments: [
    {
      id: "expand-appointment-menu",
      title: "Step 1: Appointments",
      description:
        "Easily create and manage appointments with ProTeams",
      target: '[data-tour="operations-nav"]',
      page: "/dashboard",
    },
    {
      id: "manage-appointments",
      title: "Step 2: Manage Appointments",
      description:
        "View all of your appointments by date, client name, and employee name. View and manage appointments in the future and the past.",
      target: '[data-tour="appointment-manage"]',
      page: "/operations/manageAppointments",
    },
    {
      id: "create-appointments",
      title: "Step 3: Create Appointments",
      description:
        "Quickly create appointments for your team. Select a client, assign shifts, tasks and employees. Create recurring or single appointments.",
      target: '[data-tour="appointment-create"]',
      page: "/operations/appoinmentSchedule",
    },
  ],
  clients: [
    {
      id: 'expand-client-menu',
      title: 'Step 1: Quick Client Creation',
      description: 'You can create clients quickly and easily with ProTeams app.',
      target: '[data-tour="people-nav"]',
      page: '/dashboard'
    },
    {
      id: 'client-create-nav',
      title: 'Step 2: Add Client Details',
      description: 'Add details about each client including name, address, logo and contact information.',
      target: '[data-tour="client-manage"]',
      page: '/peoples/createClient'
    },
    // {
    //   id: 'client-features',
    //   title: 'Step 3: Client Features',
    //   description: 'You can invite client contacts to your account for future communication. And you can create unique shifts and tasks for each client.',
    //   target: '[data-tour="client-create"]',
    //   page: '/dashboard'
    // },
    {
      id: 'client-manage-nav',
      title: 'Step 3: Manage Clients',
      description: 'You can also modify and delete client information by clicking manage clients.',
      target: '[data-tour="client-manage"]',
      page: '/peoples/manageClient'
    }
  ],
  employee: [
    {
      id: 'expand-teams-menu',
      title: 'Step 1: Adding Team Members',
      description: 'Adding employees and contract employees is simple in ProTeams app.',
      target: '[data-tour="people-nav"]',
      page: '/dashboard'
    },
    {
      id: 'team-create-nav',
      title: 'Step 2: Add Employee Details',
      description: 'Add details about each employee including name, email, phone and related contact information.',
      target: '[data-tour="team-manage"]',
      page: '/peoples/createEmp'
    },
    // {
    //   id: 'team-roles',
    //   title: 'Step 3: Assign Roles',
    //   description: 'Assign distinct roles and rights to each employee based on their profile (Support, Admin, Client Team).',
    //   target: '[data-tour="team-create"]',
    //   page: '/dashboard'
    // },
    {
      id: 'team-manage-nav',
      title: 'Step 3: Manage Teams',
      description: 'You can also modify and delete your team\'s information by clicking manage teams.',
      target: '[data-tour="team-manage"]',
      page: '/peoples/manageEmp'
    },
    {
      id: 'team-groups',
      title: 'Step 4: Assign Groups',
      description: 'Assign your team to groups to make it easy to manage and communicate with groups of employees.',
      target: '[data-tour="team-manage"]',
      page: '/peoples/manageGroups'
    },
  ],
  supplies: [
    {
      id: 'expand-supplies-menu',
      title: 'Step 1: Supply Request Management',
      description: 'Review, approve or reject your team\'s supply requests with one click in ProTeams.',
      target: '[data-tour="operations-nav"]',
      page: '/dashboard'
    },
    {
      id: 'supplies-new-requests-nav',
      title: 'Step 2: New Requests',
      description: 'Manage approved requests, update the status of items and orders, and update the statuses in real-time to your field team.',
      target: '[data-tour="supplies-new"]',
      page: '/supplies/cleaning/review'
    },
    {
      id: 'supplies-manage-nav',
      title: 'Step 3: Order Management',
      description: 'Add additional notes to an order request and share it with your team so they are kept up to date on order status.',
      target: '[data-tour="supplies-manage"]',
      page: '/supplies/cleaning/manage'
    },
    {
      id: 'supplies-archived-nav',
      title: 'Step 4: Archive Orders',
      description: 'Archive completed orders as needed.',
      target: '[data-tour="supplies-manage"]',
      page: '/supplies/cleaning/archives'
    }
  ],
  issues: [
    {
      id: 'expand-operations-menu-issues',
      title: 'Step 1: Create New Issues',
      description: 'Creating a new issues for your team has never been simpler. Start by clicking the "Create Issue" button.',
      target: '[data-tour="operations-nav"]',
      page: '/dashboard'
    },
    {
      id: 'issues-manage-nav',
      title: 'Step 2: Manage Issues',
      description: 'View and manage existing issues raised by your field team directly from your web portal access.',
      target: '[data-tour="issues-manage"]',
      page: '/operations/issues/manage'
    },
    {
      id: 'issues-detail-nav',
      title: 'Step 3: Issue Details',
      description: 'View additional details and add notes to an issue with the click of a button.',
      target: '[data-tour="issues-manage"]',
      page: '/operations/issues/manage'
    },
    {
      id: 'issues-archive-nav',
      title: 'Step 4: Archive Issues',
      description: 'Archive closed issues to stay organized.',
      target: '[data-tour="issues-manage"]',
      page: '/operations/issues/archive'
    }
  ],
  messages: [
    {
      id: 'live-chat-nav',
      title: 'Step 1: Live Chat',
      description: 'Start by clicking on Live and choose a private or public channel to start a conversations. Private channels messages are viewable only to active members in the group.',
      target: '[data-tour="communications-nav"]',
      page: '/dashboard'
    },
    {
      id: 'create-channel-nav',
      title: 'Step 2: Create Channels',
      description: 'Before you can chat, you must first create a channel and invite active teams members to to the channel. Team member can include coworkers, clients support teams, and management.',
      target: '[data-tour="manage-channels"]',
      page: '/communications/message-board/create-channel'
    },
    {
      id: 'manage-channels-nav',
      title: 'Step 3: Manage Channels',
      description: 'If you want to edit, delete or modify a channel in any way, select manager channels and perform the desired actions',
      target: '[data-tour="manage-channels"]',
      page: '/communications/message-board/manage-channel'
    },
    {
      id: 'channel-messaging',
      title: 'Step 4: Real-time Communication',
      description: 'Once a channel has been created, you and active members can post and receive messages to the channel for real time communications.',
      target: '[data-tour="live-chat"]',
      page: '/message-board/live-chat'
    },
  ],
  support: [
    {
      id: 'support-help-nav',
      title: 'Step 1: Access Support',
      description: 'If you have any issues with the ProTeams app you can easily raise a support requests for support in the ProTeams app',
      target: '[data-tour="help-nav"]',
      page: '/dashboard'
    },
    {
      id: 'support-manage-nav',
      title: 'Step 2: Create and Manage Tickets',
      description: 'After you have navigated from help to support you can create a new support ticket or you can manage existing support tickets',
      target: '[data-tour="support-manage"]',
      page: '/help/support'
    },
    {
      id: 'support-details',
      title: 'Step 3: Ticket Details',
      description: 'You can view more detail about your opened support ticket, attach files, and add additional information as needed',
      target: '[data-tour="support-manage"]',
      page: '/help/manageSupport'
    }
  ],
  reports: [
    {
      id: 'report-help-nav',
      title: 'Step 1: Reporting Tools',
      description: 'ProTeams provides a range of powerful reports that can yield impactful insights about your business and operations.',
      target: '[data-tour="report-nav"]',
      page: '/dashboard'
    },
    {
      id: 'report-client-nav',
      title: 'Step 2: Reporting Categories',
      description: 'You can select from range of reports designed to yield key data points about your teams and your clients.',
      target: '[data-tour="client-report"]',
      page: '/reports/clientreport'
    },
    {
      id: 'report-team-nav',
      title: 'Step 3: Customize Reports',
      description: 'Select a desired report and customize your report by start date, end date and other data fields.',
      target: '[data-tour="team-report"]',
      page: '/reports/checkInCheckOut'
    },
    {
      id: 'report-team-nav',
      title: 'Step 4: View and Share Results',
      description: 'Once you have your results you can easily view them, download them and email them as PDFs, CSV, or XLSX.',
      target: '[data-tour="team-report"]',
      page: '/reports/checkInCheckOut'
    }
  ]
};

export const TourProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentTourId, setCurrentTourId] = useState(null);
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const currentPage = window.location.pathname;

  const shouldShowInitialTour = () => {
    const restrictedPages = [
      "/login",
      "/register",
      "/forgotpassword",
      "/updatepassword",
      "/verify-email",
      "/verify-email-sent",
    ];

    // Do not show tours while the Unauthorized page is being rendered,
    // even if the URL points to a secure route like /dashboard.
    const isUnauthorizedPage =
      typeof document !== "undefined" &&
      !!document.querySelector('[data-page="unauthorized"]');

    if (isUnauthorizedPage) {
      return false;
    }

    if (restrictedPages?.some((page) => currentPage?.startsWith(page))) {
      return false;
    }
    return true;
  };

  const startTour = useCallback((tourId = "getting-started") => {
    setCurrentTourId(tourId);
    setIsActive(true);
    setCurrentStep(0);
  }, []);

  const stopTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    if (currentTourId === "getting-started") {
      localStorage.setItem("hasSeenTour", "true");
    }
    localStorage.setItem('startTour', '');
    setCurrentTourId(null);
  }, [currentTourId]);

  // Auto-start tour if a page transition (eg email verification redirect) set a one-time flag.
  // Uses sessionStorage for "only once per tab session" behavior; keeps backward-compat with localStorage.
  useEffect(() => {
    const pendingTourId =
      sessionStorage.getItem("startTour") || localStorage.getItem("startTour");

    // If we're on an unauthorized page, don't start a tour even if a flag exists.
    const isUnauthorizedPage =
      typeof document !== "undefined" &&
      !!document.querySelector('[data-page="unauthorized"]');

    if (isUnauthorizedPage) return;

    if (!pendingTourId) return;

    // Consume the flag so refreshes don't re-trigger.
    sessionStorage.removeItem("startTour");
    localStorage.removeItem("startTour");

    if (!shouldShowInitialTour()) return;

    const timer = setTimeout(() => {
      startTour(pendingTourId);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentPage, startTour]);

  // Redundant initial tour trigger removed to prevent duplicate/premature starts.
  // App.js now handles the "getting-started" tour trigger based on full data readiness.
  useEffect(() => {
    const hasSeenTour = localStorage.getItem("hasSeenTour");
    if (!hasSeenTour && shouldShowInitialTour()) {
      const timer = setTimeout(() => {
        startTour("getting-started");
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [startTour]);

  const getCurrentTourSteps = () => {
    return currentTourId ? tourData[currentTourId] || [] : [];
  };

  const navigateToStepPage = (step) => {
    const steps = getCurrentTourSteps();
    if (step < steps.length) {
      const targetStep = steps[step];
      const targetPage = targetStep.page;

      if (currentPage !== targetPage) {
        navigate(targetPage);
        setTimeout(() => {
          setCurrentStep(step);
        }, 1000);
      } else {
        setCurrentStep(step);
      }
    }
  };

  useEffect(() => {
    if (isActive && currentTourId === "dashboard") {
      const steps = getCurrentTourSteps();
      const currentStepData = steps[currentStep];

      if (currentStepData) {
        if (currentPage !== currentStepData.page) {
          if (currentStep === 0) {
            navigate(currentStepData.page);
            return;
          } else {
            stopTour();
            return;
          }
        }

        const timer = setTimeout(() => { }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [currentPage, isActive, currentStep]);

  const nextStep = () => {
    const steps = getCurrentTourSteps();
    if (currentStep < steps.length - 1) {
      if (currentTourId !== "getting-started") {
        const nextStepIndex = currentStep + 1;
        navigateToStepPage(nextStepIndex);
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      stopTour();
    }
  };

  const previosToStepPage = (step) => {
    const steps = getCurrentTourSteps();
    if (step < steps.length) {
      const targetStep = steps[step];
      const targetPage = targetStep.page;

      if (currentPage !== targetPage) {
        navigate(targetPage);
        setTimeout(() => {
          setCurrentStep(step);
        }, 1000);
      } else {
        setCurrentStep(step);
      }
    }
  };

  const prevStep = () => {
    if (currentTourId !== "getting-started") {
      const nextStepIndex = currentStep - 1;
      previosToStepPage(nextStepIndex);
    } else {
      if (currentStep > 0) {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const goToStep = (step) => {
    const steps = getCurrentTourSteps();
    if (step >= 0 && step < steps.length) {
      setCurrentStep(step);
    }
  };

  const tourSteps = getCurrentTourSteps();
  const currentTourStep =
    isActive && tourSteps.length > 0 ? tourSteps[currentStep] : null;

  return (
    <TourContext.Provider
      value={{
        isActive,
        currentStep,
        totalSteps: tourSteps.length,
        startTour,
        stopTour,
        nextStep,
        prevStep,
        goToStep,
        currentTourStep,
        currentTourId,
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};
